/**
 * Browser client entry point: connection, login, input and the render loop.
 * The client never decides rules; it only sends commands and draws the PlayerView it receives.
 */
import type { ActionTarget, Command, ServerMessage } from '../shared/protocol.ts';
import type { ActionView, CreatureView, CueView, DungeonView, PickupItemView, PlayerView, RollView } from '../shared/view.ts';
import { builderOpen, closeBuilder, installBuilder, onBuildPreview, onBuilderError, onBuilderState, openBuilder } from './builder.ts';
import { DiceTray } from './dice.ts';
import { doorRun, doorSquares, trappedDoor } from './doors.ts';
import { Effects } from './fx.ts';
import { LogPopout } from './popout.ts';
import { Connection } from './net.ts';
import { newWalk, walkStep, type Point, type Walk } from './pathing.ts';
import { MapRenderer, type Overlay } from './render.ts';
import { blockHtml, installRulesTooltip } from './rules.ts';
import { SoundBoard, type SoundName } from './sound.ts';
import type { Attribute } from '../engine/rules/attributes.ts';
import { applyControl, newListState } from './picklist.ts';
import { activeActions, actorOf, controlledId, me, store } from './store.ts';
import { openTokenizer } from './tokenizer.ts';
import { openNameDialog } from './naming.ts';
import { feedPicks, pointBlankPicks } from './picks.ts';
import { TurnAlert, turnGlow } from './turn-alert.ts';
import {
  actionBarList, avatar, banner, boostsNeeded, covers, enchantable, enchantCommand, esc, installRenderGuard, moveAction, nextDebilitationOrder, renderActionbar, renderCamp, renderEnchant,
  renderAmmo, renderBag, renderEnchantRequests, renderHub, renderHud, renderLog, renderModal, renderParty, renderKickVote, renderDescentHold, renderPickup, renderScavenge, renderSheet, renderTalk, renderTopbar,
  renderTurnbar, resetActionOrder, sellAskStands, sellClickStep, setHtml, toast, tooltipFor, yourAutopilot, lootState, setLootCollapsed,
} from './ui.ts';
import { degreePhrase, matterLabel, matterTone } from './chat.ts';
import { currentTalk, recallQuestion, talkToBand, talkToCreature } from './talk.ts';
import { renderJournal } from './journal.ts';
import { helperBack, renderEnchantHelper } from './enchant-helper.ts';
import { escapeTransfer, installTransfer, renderTransfer } from './transfer.ts';

const $ =<T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const room = new URLSearchParams(location.search).get('room') ?? 'main';
const token = (() => {
  let t = localStorage.getItem('crawler.token');
  if (!t) {
    t = crypto.randomUUID();
    localStorage.setItem('crawler.token', t);
  }
  return t;
})();

const conn = new Connection(room);
const renderer = new MapRenderer($<HTMLCanvasElement>('map'), Number(localStorage.getItem('crawler.zoom')) || 40);
const diceTray = new DiceTray($('dice-tray'));
installRulesTooltip(() => store.welcome?.codex ?? null);
installRenderGuard();
// The Give menu (client/transfer.ts) handles its own clicks and its chooser box.
installTransfer(send);

// ------------------------------------------------------------ sound

const sound = new SoundBoard();

/** The sting when a roll's dice land. Hits get their thud from the damage roll that follows. */
function rollSound(r: RollView): SoundName | null {
  switch (r.kind) {
    case 'attack': return r.degree === 3 ? 'crit' : r.degree === 2 ? null : 'miss';
    case 'damage': return 'hit';
    case 'heal': return 'heal';
    case 'spell': return (r.degree ?? 0) >= 2 ? 'spell' : 'spell-fail';
    case 'encounter': case 'rest': return r.degree === 1 ? 'ominous' : null;
    case 'initiative': return null;
    default:
      if (r.degree === undefined) return null;
      if (r.good !== undefined) return r.good ? 'good' : 'bad';
      return r.degree >= 2 ? 'good' : 'bad';
  }
}
diceTray.onStart = (r) => {
  sound.play('dice', 0, Math.min(6, r.dice.length));
  aimFor(r);
};
diceTray.onFinish = (r) => {
  const s = rollSound(r);
  if (s) sound.play(s);
  animateRoll(r);
  matterPopup(r);
};

/** "Modifiers matter" on the map: a roll you (or your construct) were part of was decided by a modifier. */
function matterPopup(r: RollView): void {
  const view = store.view;
  if (!r.mattered?.length || r.degree === undefined || !view) return;
  const mine = new Set([me(view)?.id, view.construct?.id].filter((x): x is string => !!x));
  if (![r.rollerId, r.actorId, r.targetId].some((id) => !!id && mine.has(id))) return;
  const box = $('matter');
  for (const m of r.mattered.slice(0, 2)) {
    const el = document.createElement('div');
    el.className = `matter-pop ${matterTone(r, m.helped)}`;
    el.innerHTML = `⚖ <b>${escHtml(matterLabel(r, m))}</b> turned ${escHtml(degreePhrase(r, m.without))} into ${escHtml(degreePhrase(r, r.degree))}`;
    box.append(el);
    while (box.children.length > 3) box.firstElementChild!.remove();
    setTimeout(() => el.remove(), 3700);
  }
}

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// ------------------------------------------------------------ map effects

const fx = new Effects();
const PARTY_AIM = 'rgba(130, 205, 255, 0.95)';
const HOSTILE_AIM = 'rgba(255, 95, 70, 0.95)';

function creatureInView(id: string | undefined): CreatureView | undefined {
  return id ? store.view?.dungeon?.creatures.find((c) => c.id === id) : undefined;
}

/** The attacker and target of a roll, when both are on your map. */
function exchange(r: RollView): [CreatureView, CreatureView] | null {
  const a = creatureInView(r.actorId), t = creatureInView(r.targetId);
  return a && t && a.id !== t.id ? [a, t] : null;
}

/** While an attack roll (or a save against a spell) tumbles, a line shows who is attacking whom. */
function aimFor(r: RollView): void {
  if (r.kind === 'damage' || r.kind === 'heal') return;
  const pair = exchange(r);
  if (pair) fx.aim(pair[0].id, pair[1].id, pair[0].faction === 'party' ? PARTY_AIM : HOSTILE_AIM, 3000);
}

/** When the dice land, the attacker lunges or looses a missile, and misses and critical hits are called out. */
function animateRoll(r: RollView): void {
  if (!r.targetId) return;
  if (r.kind === 'damage') {
    // The damage number appears as these dice land (it was scheduled for about now).
    const dealt = r.damage?.reduce((sum, d) => sum + d.amount, 0);
    const shown = fx.release(r.targetId, { size: (r.multiplier ?? 1) > 1 ? 1.45 : undefined });
    const t = creatureInView(r.targetId);
    if (!shown && dealt === 0 && t) fx.float('0', '#bdbdbd', t, { creatureId: t.id, size: 0.85 });
    return;
  }
  if (r.actorId) fx.endAim(r.actorId);
  const pair = exchange(r);
  if (!pair) return;
  const [a, t] = pair;
  if (r.ranged) {
    const magic = r.kind === 'save' || r.label.startsWith('Spell');
    fx.lunge(a.id, a, t, 0.12);
    fx.missile(a, t, magic ? '#c9a0ff' : a.faction === 'party' ? '#bfe6ff' : '#ffc08a');
  } else {
    fx.lunge(a.id, a, t);
  }
  if (r.kind !== 'attack' || r.degree === undefined) return;
  if (r.degree <= 1) fx.float(r.degree === 0 ? 'Critical Miss' : 'Miss', '#d2d2d2', t, { creatureId: t.id, size: 0.72 });
  else if (r.degree === 3) fx.float('Critical Hit!', '#ffd24a', t, { creatureId: t.id, size: 0.78 });
}

/** Floating damage and healing numbers, and a skull where something dies. */
function cueEffect(view: PlayerView, c: CueView, delay: number): void {
  const d = view.dungeon;
  if (!d || (c.cue !== 'hurt' && c.cue !== 'heal' && c.cue !== 'death')) return;
  const creature = c.by ? d.creatures.find((x) => x.id === c.by) : undefined;
  const at = creature ?? c.at;
  if (!at) return;
  // Nothing appears for creatures the party can't perceive.
  if (!creature && !['1', '2'].includes(d.vis[at.y * d.width + at.x])) return;
  if (c.cue === 'hurt' && c.amount) fx.float(`−${c.amount}`, '#ff4636', at, { creatureId: c.by, delay, hit: true });
  else if (c.cue === 'heal' && c.amount) fx.float(`+${c.amount}`, '#62e27c', at, { creatureId: c.by, delay });
  else if (c.cue === 'death') fx.float('💀', '#ffffff', at, { creatureId: c.by, delay: delay + 150, size: 1.15 });
}

const CUE_SOUND: Partial<Record<CueView['cue'], SoundName>> = {
  'door-open': 'door-open', 'door-close': 'door-close', 'light-lit': 'light', 'light-fumble': 'light-fumble', 'light-out': 'light-out',
  'item-drop': 'drop', loot: 'loot', death: 'death', 'level-up': 'level-up', encounter: 'encounter', victory: 'victory',
  listen: 'listen', shrine: 'shrine', hurt: 'hurt', fall: 'fall', splash: 'splash', glyph: 'glyph', rubble: 'rubble',
  unlock: 'unlock', speech: 'speech', quest: 'quest', 'spellstrike-fumble': 'spellstrike-fumble',
};

/** Lights the board while it's your encounter turn (a class only, so no panel's HTML changes with it). */
function showTurnGlow(view: PlayerView): void {
  $('board').classList.toggle('your-turn', turnGlow(view) === 'combat');
}

let lastCue = -1;
/** Plays new cues; ones that follow dice rolls wait until the dice tray has shown them. */
function playCues(view: PlayerView): void {
  if (lastCue < 0) {
    lastCue = view.cues.at(-1)?.seq ?? 0;
    return;
  }
  const delay = diceTray.backlogMs(performance.now());
  const you = me(view)?.id;
  for (const c of view.cues) {
    if (c.seq <= lastCue) continue;
    lastCue = c.seq;
    cueEffect(view, c, delay);
    if (c.cue === 'hurt' && c.by !== you) continue;
    const name = CUE_SOUND[c.cue];
    if (name) sound.play(name, c.cue === 'encounter' ? 0 : delay);
  }
}

function renderSoundControls(): void {
  const btn = $('sound-toggle');
  btn.textContent = sound.muted ? '🔇' : '🔊';
  btn.title = sound.muted ? 'Sound off (click to turn on)' : 'Sound on (click to mute)';
  $<HTMLInputElement>('sound-volume').value = String(sound.volume);
}
$('sound-toggle').addEventListener('click', () => {
  sound.setMuted(!sound.muted);
  renderSoundControls();
  sound.play('good');
});
$<HTMLInputElement>('sound-volume').addEventListener('input', (e) => {
  sound.setVolume(Number((e.target as HTMLInputElement).value));
  if (sound.muted) sound.setMuted(false);
  renderSoundControls();
});
$<HTMLInputElement>('sound-volume').addEventListener('change', () => sound.play('dice', 0, 2));
renderSoundControls();

function zoom(step: number): void {
  const tile = renderer.zoom(step);
  localStorage.setItem('crawler.zoom', String(tile));
  const hint = $('zoom-hint');
  hint.textContent = `Zoom ${Math.round((tile / 40) * 100)}%`;
  hint.classList.add('show');
  window.clearTimeout(zoomHintTimer);
  zoomHintTimer = window.setTimeout(() => hint.classList.remove('show'), 900);
}
let zoomHintTimer = 0;

function send(cmd: Command): void {
  conn.send({ t: 'cmd', cmd });
}

function playerName(): string {
  return ($<HTMLInputElement>('player-name').value || localStorage.getItem('crawler.name') || 'Delver').trim();
}

// ------------------------------------------------------------ connection

conn.onStatus = (s) => {
  const el = $('status');
  el.textContent = s === 'open' ? `connected · room "${room}"` : s === 'connecting' ? 'connecting…' : 'disconnected — retrying';
  el.className = `status ${s === 'open' ? 'ok' : s === 'closed' ? 'bad' : ''}`;
};
conn.onOpen = () => conn.send({ t: 'hello', token, name: playerName() });
conn.onMessage = (msg: ServerMessage) => {
  switch (msg.t) {
    case 'welcome':
      store.playerId = msg.playerId;
      store.welcome = msg;
      renderLogin();
      showScreen();
      break;
    case 'characters':
      if (store.welcome) store.welcome.characters = msg.characters;
      renderLogin();
      break;
    case 'joined':
      closeBuilder();
      store.joined = true;
      store.showLogin = false;
      showScreen();
      break;
    case 'state':
      onBuilderState();
      onView(msg.view);
      break;
    case 'buildPreview':
      onBuildPreview(msg.preview);
      break;
    case 'retrainOptions':
      store.retrainOptions = { kind: msg.kind, remove: msg.remove, options: msg.options };
      if (store.view) renderHub(store.view);
      break;
    case 'error':
      onBuilderError();
      toast(msg.message);
      break;
    case 'kicked':
      conn.stop();
      showKicked(msg.message);
      break;
  }
};

/** The party removed us: say so, and stay disconnected. */
function showKicked(message: string): void {
  const modal = $('modal');
  setHtml(modal, `<div class="modal-box kicked"><h2>Removed from the game</h2><p>${esc(message)}</p><button type="button" class="primary" onclick="location.reload()">Reload</button></div>`);
  modal.classList.remove('hidden');
  const status = $('status');
  status.textContent = 'removed from the game';
  status.className = 'status bad';
}

// ------------------------------------------------------------ login / character creation

installBuilder({ root: $('builder'), welcome: () => store.welcome, send: (m) => conn.send(m), cmd: (c) => send(c), toast });

function renderLogin(): void {
  const w = store.welcome;
  if (!w) return;
  const nameInput = $<HTMLInputElement>('player-name');
  if (!nameInput.value) nameInput.value = localStorage.getItem('crawler.name') ?? '';
  setHtml($('char-list'), w.characters.length
    ? w.characters.map((c) => `<span class="char-card-wrap"><button type="button" class="char-card ${c.dead ? 'dead' : ''}" data-select="${esc(c.id)}" ${c.dead ? 'disabled' : ''}>${avatar(c.token, c.name, 'large')}<span><b>${esc(c.name)}</b><br><span class="muted">${esc(c.ancestry)} ${esc(c.className)} · level ${c.level}${c.dead ? ' · dead' : ''}</span></span></button><button type="button" class="small ghost token-edit" data-token-edit="${esc(c.id)}" title="Change token">Token</button></span>`).join('')
    : '<span class="muted">You have no characters. Create one below.</span>');
}

function showScreen(): void {
  const login = !store.joined || store.showLogin;
  $('login').classList.toggle('hidden', !login);
  $('game').classList.toggle('hidden', login);
  $('switch-char').classList.toggle('hidden', !store.joined);
}

$('player-name').addEventListener('change', () => {
  localStorage.setItem('crawler.name', playerName());
  conn.send({ t: 'hello', token, name: playerName() });
});

$('switch-char').addEventListener('click', () => {
  store.showLogin = !store.showLogin;
  showScreen();
});

/** Opens or closes your character's journal. */
function toggleJournal(): void {
  store.journalOpen = !store.journalOpen;
  if (store.view) renderJournal(store.view);
}

$('journal-toggle').addEventListener('click', toggleJournal);

// ------------------------------------------------------------ view updates

let previous: PlayerView | null = null;
const turnAlert = new TurnAlert();

// ------------------------------------------------------------ the log in its own window

const popout = new LogPopout({
  room,
  view: () => store.view,
  codex: () => store.welcome?.codex ?? null,
  // Words reach whoever is in earshot, or ask your Recall Knowledge question when the chat box is set to.
  say: (text) => send(recallQuestion(store.view, text) ?? { type: 'say', text }),
  onChange: (open) => $('board').classList.toggle('log-popped', open),
});
function popOutLog(): void {
  const result = popout.pop();
  if (result === 'blocked') toast('Your browser blocked the pop-up window. Allow pop-ups for this site to open the log in its own window.');
  else if (result === 'unsupported') toast("This browser can't open the log in its own window. Try Chrome, Edge or Firefox.");
}
$('log-popout').addEventListener('click', popOutLog);
$('log-show').addEventListener('click', popOutLog);
$('log-dock').addEventListener('click', () => popout.dock());

/** What each creature said last, shown in a bubble over its token for a few seconds (longer words, longer). */
const bubbles = new Map<string, { text: string; at: number; ms: number }>();
let speechSeq = -1;

/** Long enough to read the whole thing: about 18 characters a second, on top of a few seconds. */
function bubbleMs(text: string): number {
  return Math.min(25000, 4000 + text.length * 55);
}

function collectSpeech(view: PlayerView): void {
  const now = performance.now();
  const newest = view.log.at(-1)?.seq ?? 0;
  // On joining, old words stay in the log, not over heads.
  if (speechSeq < 0) speechSeq = newest;
  for (const l of view.log) {
    if (l.seq <= speechSeq || !l.speech) continue;
    const quote = /“([^”]*)”/.exec(l.text)?.[1] ?? l.text;
    bubbles.set(l.speech.by, { text: quote, at: now, ms: bubbleMs(quote) });
  }
  speechSeq = Math.max(speechSeq, newest);
  for (const [id, b] of bubbles) if (now - b.at > b.ms) bubbles.delete(id);
}

function onView(view: PlayerView): void {
  const prev = previous;
  previous = view;
  store.view = view;
  collectSpeech(view);
  if (view.prompt && view.prompt.id !== prev?.prompt?.id) sound.play('turn');
  diceTray.sync(view.rolls);
  playCues(view);
  // Your encounter turn: a banner and its own chime, once per turn however many views arrive during
  // it, and the board lit while it lasts. Outside encounters there are no turns: nothing lights up.
  const announce = turnAlert.next(view);
  const started = !prev?.combat && !!view.combat;
  if (started) banner('Roll Initiative');
  if (announce) {
    const backlog = diceTray.backlogMs(performance.now());
    // Going first: Roll Initiative stays up while the initiative dice land, then your turn.
    if (started) window.setTimeout(() => { if (store.view?.combat?.yourTurn) banner('Your Turn', 'turn'); }, Math.max(1400, backlog));
    else banner('Your Turn', 'turn');
    sound.play('your-turn', backlog);
  }
  showTurnGlow(view);
  if (prev && prev.dungeon && view.dungeon && prev.dungeon.depth !== view.dungeon.depth) banner(`Depth ${view.dungeon.depth}`);
  // The dungeon's line for a floor it shaped, once, on arriving there.
  if (view.dungeon?.arrival && (prev?.dungeon?.depth !== view.dungeon.depth || prev?.phase !== 'dungeon')) toast(view.dungeon.arrival);
  if (prev?.phase === 'dungeon' && view.phase === 'hub') banner('Returned to Town');
  if (view.combat || view.phase === 'hub') {
    store.walkTo = null;
    store.pickupAt = null;
    store.openDoorAt = null;
  }
  // The loot panel's collapses are for this delve's piles.
  if (view.phase === 'hub') store.lootDismissed.clear();
  // A Sell question with nowhere to draw (you left town, the merchant is out of earshot, the item went) is dropped:
  // otherwise it comes back unasked on that row next time, maybe at another buyer's price.
  if (store.sellConfirm && !sellAskStands(view, store.sellConfirm)) store.sellConfirm = null;
  if (store.controlling && (!view.combat?.yourTurn || !view.construct || view.construct.actionsLeft <= 0)) {
    store.controlling = false;
    if (store.targeting?.actorId) cancelTargeting();
  }
  // Your last action went into a Command: your turn is now the construct's, so clicks go to it.
  const k = view.construct;
  if (!store.controlling && view.combat?.yourTurn && view.combat.actionsLeft <= 0 && k?.ready && k.actionsLeft > 0 && !(prev?.combat?.yourTurn && prev.combat.actionsLeft <= 0 && prev.construct?.actionsLeft === k.actionsLeft)) {
    store.controlling = true;
    if (store.targeting) cancelTargeting();
  }
  if (store.targeting && !activeActions(view).some((a) => a.id === store.targeting!.action.id && a.enabled)) cancelTargeting();
  const inHub = view.phase === 'hub';
  $('map-wrap').classList.toggle('hidden', inHub);
  $('hub').classList.toggle('hidden', !inHub);
  $('actionbar').classList.toggle('hidden', inHub);
  renderTopbar(view);
  renderTurnbar(view);
  renderHud(view);
  renderAmmo(view);
  renderTalk(view);
  renderActionbar(view);
  renderParty(view);
  renderSheet(view);
  renderBag(view);
  renderHub(view);
  renderLog(view);
  popout.update(view);
  renderModal(view);
  renderCamp(view);
  renderTransfer(view);
  renderJournal(view);
  renderEnchant(view);
  renderEnchantHelper(view);
  renderEnchantRequests(view);
  renderScavenge(view);
  renderKickVote(view);
  renderDescentHold(view);
  renderPickup(view);
  refreshTooltip(view);
}

// ------------------------------------------------------------ picking things up

function chebyshev(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** Opens the loot panel (collapsed or not) on what lies within reach, `focus` (the square clicked) first. */
function expandLoot(view: PlayerView, focus: { x: number; y: number } | null): void {
  setLootCollapsed(view, false);
  store.pickupFocus = focus;
  renderPickup(view);
}

/** Collapses the loot panel to its bar until something new comes within reach. */
function collapseLoot(view: PlayerView): void {
  setLootCollapsed(view, true);
  store.pickupFocus = null;
  renderPickup(view);
}

/** Takes an item within reach: as much of the stack as fits (all of it when `qty` is omitted). */
function takeItem(i: PickupItemView, qty = i.fits): void {
  send({ type: 'action', actionId: 'pick-up', target: { itemUid: i.uid, ...(qty < i.qty ? { qty } : {}) } });
}

/**
 * Picking up from the square clicked. A lone item that fits goes straight into your pack; a pile, a stack (take 1 or
 * all of it), a structure on the square (stairs, a campfire), or a stack too big for your pack points the loot panel
 * at it (opening it if it was collapsed) so you can choose.
 */
function pickUp(view: PlayerView, at: { x: number; y: number }): void {
  const pool = (view.pickup?.squares ?? []).filter((s) => s.x === at.x && s.y === at.y);
  const items = pool.flatMap((s) => s.items);
  if (!items.length) return toast('There is nothing within reach to pick up');
  const only = items.length === 1 ? items[0] : null;
  const stack = !!only && !only.gold && only.qty > 1;
  if (only && only.fits >= only.qty && !stack && !pool[0].feature?.interactive) return takeItem(only);
  expandLoot(view, at);
}

// ------------------------------------------------------------ dropping some of a stack

function openDrop(view: PlayerView, itemUid: string | null): void {
  store.dropOpen = itemUid;
  store.dropTyped = '';
  renderSheet(view);
  renderBag(view);
  if (itemUid) document.querySelector<HTMLInputElement>('[data-drop-input]')?.focus();
}

/**
 * Drops some of a stack from the Drop chooser: `how` is "1", "all", or "typed" (the number in its
 * box, a whole number from 1 to the stack's size; the engine checks it again).
 */
function dropSome(view: PlayerView, itemUid: string, how: string): void {
  const item = view.sheet?.items.find((i) => i.uid === itemUid);
  if (!item) return openDrop(view, null);
  let qty: number | undefined = how === 'all' ? undefined : Number(how);
  if (how === 'typed') {
    const box = document.querySelector<HTMLInputElement>(`[data-drop-input="${CSS.escape(itemUid)}"]`);
    qty = Number((box?.value || store.dropTyped).trim());
    if (!Number.isInteger(qty) || qty < 1 || qty > item.qty) return toast(`Type a number from 1 to ${item.qty}`);
  }
  send({ type: 'action', actionId: 'drop', target: { itemUid, ...(qty !== undefined && qty < item.qty ? { qty } : {}) } });
  openDrop(view, null);
}

document.addEventListener('input', (e) => {
  const el = e.target;
  if (el instanceof HTMLInputElement && el.dataset.dropInput) store.dropTyped = el.value;
});
document.addEventListener('keydown', (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !el.dataset.dropInput || !store.view) return;
  if (e.key === 'Enter') {
    e.preventDefault();
    dropSome(store.view, el.dataset.dropInput, 'typed');
  } else if (e.key === 'Escape') openDrop(store.view, null);
});

// ------------------------------------------------------------ selling (asks first)

/** Opens or closes the Sell question on a gear row. */
function askSell(view: PlayerView, ask: typeof store.sellConfirm): void {
  store.sellConfirm = ask;
  renderSheet(view);
  renderBag(view);
}

/**
 * Sell, in town or to a merchant: the first click asks (the button turns into "Sell for N gp? Sell ✕"); its Sell, or a
 * Shift+click, sells one. A double-click only asks: its second click lands on the question's Sell, which draws where
 * the button was (`sellClickStep`). The question isn't focused: Enter would end your encounter turn as well as press it.
 */
function sellClick(view: PlayerView, ds: DOMStringMap, shift: boolean, detail: number): void {
  const how = ds.sell ? 'town' : 'trade';
  const uid = (ds.sell ?? ds.tradeSell)!;
  const step = sellClickStep(ds.sellSure !== undefined, shift, detail);
  if (step === 'ignore') return;
  if (step === 'ask') return askSell(view, { uid, how });
  if (store.sellConfirm) askSell(view, null);
  if (how === 'town') send({ type: 'sell', itemUid: uid });
  else send({ type: 'trade', action: 'sell', itemUid: uid });
}

/** Where to walk to reach a square: the square itself, or the nearest open square next to it (a brazier, a chest). */
function standNear(d: DungeonView, target: { x: number; y: number }, you: { x: number; y: number }): { x: number; y: number } {
  const open = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= d.width || y >= d.height) return false;
    const t = d.tiles[y * d.width + x];
    return (t === '.' || t === "'") && !d.features.some((f) => f.solid && f.x === x && f.y === y)
      && !d.creatures.some((c) => !c.isYou && c.health !== 'dead' && covers(c, x, y));
  };
  if (open(target.x, target.y)) return target;
  let best: { x: number; y: number } | null = null;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const p = { x: target.x + dx, y: target.y + dy };
    if ((dx || dy) && open(p.x, p.y) && (!best || chebyshev(p, you) < chebyshev(best, you))) best = p;
  }
  return best ?? target;
}

// ------------------------------------------------------------ targeting

/** Takes control of your construct (its actions, on your turn) or gives it back to your character. */
function setControl(view: PlayerView, on: boolean): void {
  const k = view.construct;
  if (on) {
    if (!k || !view.combat?.yourTurn) return;
    if (!k.ready) return toast(`${k.name} is broken down. Repair it after the encounter.`);
    if (k.actionsLeft <= 0) return toast(k.actions.find((a) => a.id === 'strike')?.reason ?? `${k.name} has no actions left this turn`);
  }
  if (store.controlling === on) return;
  store.controlling = on;
  if (store.targeting) cancelTargeting();
  renderActionbar(view);
  renderHud(view);
}

function cancelTargeting(): void {
  store.targeting = null;
  $('targeting').classList.add('hidden');
  if (store.view) renderActionbar(store.view);
}

/** The banner's options for a choice, numbered for the 1–9 keys (`pickChoice`). `title`: an enabled option's tooltip. */
function showPrompt(title: string, options: { label: string; disabled?: string; title?: string; pick: () => void }[]): void {
  const t = $('targeting');
  t.replaceChildren();
  const label = document.createElement('span');
  label.textContent = `${title}: choose `;
  t.append(label);
  options.forEach((o, n) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'choice';
    if (n < 9) {
      const key = document.createElement('kbd');
      key.textContent = String(n + 1);
      b.append(key);
    }
    b.append(o.label);
    if (o.disabled) {
      b.disabled = true;
      b.title = o.disabled;
    } else if (o.title) b.title = o.title;
    b.addEventListener('click', o.pick);
    t.append(b);
  });
  const hint = document.createElement('span');
  hint.className = 'hint';
  hint.textContent = '(1–9 to choose, Esc to cancel)';
  t.append(hint);
  t.classList.remove('hidden');
}

/** The 1–9 keys on the banner's options: the nth one, or why it can't be picked. */
function pickChoice(n: number): void {
  const b = $('targeting').querySelectorAll<HTMLButtonElement>('button.choice')[n - 1];
  if (!b) return;
  if (b.disabled) toast(b.title || "You can't choose that right now");
  else b.click();
}

/** An action that asks for a choice first (the condition a theory predicts): the banner lists its options. */
function showChoices(action: ActionView): void {
  showPrompt(action.name, (action.choices ?? []).map((c) => ({
    label: c.label,
    disabled: c.disabled,
    pick: () => {
      const tg = store.targeting;
      if (tg?.choosing && tg.action.id === action.id) startAction(tg.action, tg.strikeKey, tg.itemUid, c.id);
    },
  })));
}

/**
 * R: Reload. With a firearm or crossbow in each hand, the banner asks which (1 main hand, 2 off hand) and loads a
 * standard round into it; otherwise it is the bar's Reload (the first weapon that can't fire, specialty rounds offered).
 */
function reloadKey(view: PlayerView): void {
  const reload = activeActions(view).find((a) => a.id === 'reload');
  if (!reload) return toast(store.controlling ? "Your construct can't reload" : "You aren't wielding a firearm or crossbow");
  const guns = (view.sheet?.items ?? []).filter((i) => i.location === 'hand' && i.chamber).sort((a, b) => (a.hand === 'off' ? 1 : 0) - (b.hand === 'off' ? 1 : 0));
  if (store.controlling || guns.length < 2 || !reload.enabled || guns.every((g) => g.chamber!.ready)) return startAction(reload);
  if (store.targeting) cancelTargeting();
  store.targeting = { action: reload, choosing: true };
  showPrompt('Reload', guns.map((g) => {
    const k = g.chamber!;
    const state = k.jammed ? 'jammed' : k.ready ? 'ready' : k.progress ? `reloading ${k.progress.done}/${k.progress.of}` : k.loaded ? 'not ready' : 'unloaded';
    return {
      label: `${g.hand === 'off' ? 'Off hand' : 'Main hand'}: ${g.name} (${state})`,
      disabled: k.ready ? `Your ${g.name} is loaded` : k.blocked,
      pick: () => {
        if (store.targeting?.choosing && store.targeting.action.id === 'reload') cancelTargeting();
        send({ type: 'action', actionId: 'reload', target: { itemUid: g.uid } });
      },
    };
  }));
  renderActionbar(view);
}

/**
 * A Strike at a foe you clicked. With a ranged weapon in your main hand (or in your off hand when the main hand makes no
 * Strike) and the foe within reach of one of your melee Strikes, the banner asks which Strike to make (picks.ts `pointBlankPicks`): the shot, each melee Strike that
 * reaches, a combination weapon's Switch. Otherwise the server picks (its default Strike).
 */
function strikeAt(view: PlayerView, creature: CreatureView): void {
  const strike = view.actions.find((a) => a.id === 'strike');
  const picks = strike && !store.controlling ? pointBlankPicks(view, creature) : null;
  if (!strike || !picks) return send({ type: 'action', actionId: 'strike', target: { creatureId: creature.id } });
  if (store.targeting) cancelTargeting();
  store.targeting = { action: strike, choosing: true };
  showPrompt(`Strike ${creature.name}`, picks.map((p) => ({
    label: p.label, disabled: p.disabled, title: p.title,
    pick: () => {
      cancelTargeting();
      if (p.switchUid) send({ type: 'action', actionId: 'switch-mode', target: { itemUid: p.switchUid } });
      else send({ type: 'action', actionId: 'strike', target: { creatureId: creature.id, strikeKey: p.strikeKey } });
    },
  })));
  renderActionbar(view);
}

/** Use Item on a potion or elixir sent for yourself, or for a creature next to you (fed to it). */
function sendUse(uid: string, creatureId?: string): void {
  send({ type: 'action', actionId: 'use-item', target: { itemUid: uid, ...(creatureId ? { creatureId } : {}) } });
}

/**
 * Use on a potion or elixir: with someone next to you who would take it (Use Item's `recipients`), the banner asks whom
 * it's for (picks.ts `feedPicks`: yourself first, then the neediest; a click on their token works too). Otherwise you
 * drink it at once.
 */
function useItemAt(view: PlayerView, uid: string): void {
  const use = view.actions.find((a) => a.id === 'use-item');
  const picks = use?.enabled ? feedPicks(view, uid) : null;
  if (!use || !picks) return sendUse(uid);
  if (store.targeting) cancelTargeting();
  store.targeting = { action: use, itemUid: uid, choosing: true };
  const name = view.sheet?.items.find((i) => i.uid === uid)?.name ?? use.name;
  showPrompt(name, picks.map((p) => ({
    label: p.label,
    pick: () => {
      cancelTargeting();
      sendUse(uid, p.creatureId);
    },
  })));
  renderActionbar(view);
}

/** B and Shift+B: open the panel (or switch to it), and close it when it is already showing. */
function toggleBag(view: PlayerView, which: 'all' | 'consumables'): void {
  if (view.phase !== 'dungeon') {
    // In town your gear is on the character sheet.
    document.querySelector('#sheet .gear-group')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return;
  }
  store.bag = store.bag === which ? null : which;
  renderBag(view);
}

function startAction(given: ActionView, strikeKey?: string, itemUid?: string, choice?: string): void {
  const view = store.view!;
  // A choice can shape the action (Technomancy Blast's cone, line or burst): aim with its area and range.
  const shaped = choice !== undefined ? given.choices?.find((c) => c.id === choice) : undefined;
  const action: ActionView = shaped?.area || shaped?.range !== undefined
    ? { ...given, ...(shaped.area ? { area: shaped.area } : {}), ...(shaped.range !== undefined ? { range: shaped.range } : {}) }
    : given;
  if (!action.enabled) {
    toast(action.reason ?? `You can't use ${action.name} right now`);
    return;
  }
  if (action.id === 'make-camp') {
    if (view.scavenge) return toast('The party is already searching for firewood (see the panel above the chat)');
    store.campOpen = true;
    renderCamp(view);
    return;
  }
  const actorId = controlledId(view);
  if (action.choices?.length && choice === undefined) {
    store.targeting = { action, strikeKey, itemUid, choosing: true, ...(actorId ? { actorId } : {}) };
    showChoices(action);
    renderActionbar(view);
    return;
  }
  const picked = choice !== undefined ? { choice } : {};
  if (store.targeting?.choosing) cancelTargeting();
  if (action.target === 'none') {
    send({ type: 'action', actionId: action.id, ...(choice !== undefined ? { target: picked } : {}), ...(actorId ? { actorId } : {}) });
    return;
  }
  // The server lists the squares worth targeting (an adjacent door, glyph, or lock): use a lone one.
  if (action.target === 'tile' && action.candidates?.length === 1) {
    send({ type: 'action', actionId: action.id, target: { ...action.candidates[0], ...picked }, ...(actorId ? { actorId } : {}) });
    return;
  }
  if (action.target === 'feature') {
    const you = me(view);
    const near = view.dungeon?.features.filter((f) => you && Math.max(Math.abs(f.x - you.x), Math.abs(f.y - you.y)) <= 1) ?? [];
    if (near.length === 1) {
      send({ type: 'action', actionId: action.id, target: { x: near[0].x, y: near[0].y, ...picked } });
      return;
    }
  }
  store.targeting = { action, strikeKey, itemUid, ...picked, ...(actorId ? { actorId } : {}) };
  const t = $('targeting');
  const what = action.target === 'tile' ? 'a square' : action.target === 'feature' ? 'an adjacent object' : action.target === 'enemy' ? 'an enemy' : action.target === 'ally' ? (action.aid ? "yourself, an ally or a creature that isn't fighting you" : 'yourself or an ally') : 'a creature';
  t.textContent = `${actorId ? `${view.construct!.name}: ` : ''}${action.name}: select ${what} (Esc to cancel)`;
  t.classList.remove('hidden');
  renderActionbar(view);
}

function gridDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = Math.abs(a.x - b.x), dy = Math.abs(a.y - b.y);
  const diag = Math.min(dx, dy);
  return Math.max(dx, dy) + Math.floor(diag / 2);
}

function targetForTile(view: PlayerView, x: number, y: number): ActionTarget | null {
  const t = store.targeting;
  if (!t) return null;
  const creature = view.dungeon?.creatures.find((c) => covers(c, x, y) && c.health !== 'dead');
  switch (t.action.target) {
    case 'enemy':
      // Neutral creatures can be attacked too (they turn hostile).
      return creature && (creature.faction === 'hostile' || creature.faction === 'neutral') ? { creatureId: creature.id, strikeKey: t.strikeKey, ...(t.itemUid ? { itemUid: t.itemUid } : {}) } : null;
    case 'ally':
      // Help (healing, first aid) reaches neutral creatures too; the server checks they aren't hostile.
      return creature && (creature.faction === 'party' || (t.action.aid && creature.faction === 'neutral')) ? { creatureId: creature.id } : null;
    case 'creature':
      return creature ? { creatureId: creature.id } : null;
    case 'tile':
    case 'feature':
      return { x, y };
    default:
      return null;
  }
}

/** The tokens of whoever you're talking to: one creature, or a band's members in view. */
function talkTargets(view: PlayerView): Set<string> {
  if (!view.dungeon) return new Set();
  const talk = currentTalk(view);
  return new Set(talk.creatureIds ?? (talk.creatureId ? [talk.creatureId] : []));
}

function overlay(view: PlayerView): Overlay {
  const now = performance.now();
  const o: Overlay = {
    reachable: new Set(), targets: new Set(), targetTiles: new Set(), areaTiles: new Set(), hover: store.hover,
    currentIds: new Set(view.combat?.order.filter((x) => x.current).map((x) => x.id) ?? []),
    lootTiles: new Set(), lootFocus: null,
    talkTargets: talkTargets(view),
    turnGlow: turnGlow(view) ? actorOf(view)?.id ?? null : null,
    bubbles: [...bubbles].filter(([, b]) => now - b.at < b.ms).map(([id, b]) => ({ id, text: b.text, age: now - b.at, ms: b.ms })),
  };
  if (lootState(view) === 'open' && view.pickup && view.dungeon) {
    for (const sq of view.pickup.squares) o.lootTiles.add(sq.y * view.dungeon.width + sq.x);
    if (store.pickupFocus) o.lootFocus = store.pickupFocus.y * view.dungeon.width + store.pickupFocus.x;
  }
  const you = actorOf(view);
  const t = store.targeting;
  if (store.controlling && view.construct) o.currentIds = new Set([view.construct.id]);
  if (t && you && view.dungeon) {
    const range = t.action.range ?? 99;
    // The Use banner: ring only the tokens a click would pick (yourself, and whoever you could feed it to).
    if (t.choosing && t.action.id === 'use-item') {
      const drinker = me(view);
      if (drinker) o.targets.add(drinker.id);
      for (const id of (t.itemUid ? view.sheet?.items.find((i) => i.uid === t.itemUid)?.feedTo : undefined) ?? t.action.recipients ?? []) o.targets.add(id);
    } else if (t.action.id === 'stride') for (const i of view.combat?.reachable ?? []) o.reachable.add(i);
    // A move with its own squares (Sneak's half-Speed reach) fills them like Stride.
    else if (t.action.traits.includes('move') && t.action.candidates) for (const p of t.action.candidates) o.reachable.add(p.y * view.dungeon.width + p.x);
    else if (t.action.candidates) {
      for (const p of t.action.candidates) o.targetTiles.add(p.y * view.dungeon.width + p.x);
    } else if (t.action.target === 'tile' || t.action.target === 'feature') {
      for (let dy = -range; dy <= range; dy++) for (let dx = -range; dx <= range; dx++) {
        const x = you.x + dx, y = you.y + dy;
        if (x >= 0 && y >= 0 && x < view.dungeon.width && y < view.dungeon.height && (dx || dy)) o.targetTiles.add(y * view.dungeon.width + x);
      }
    } else {
      for (const c of view.dungeon.creatures) {
        if (c.health === 'dead' || gridDistance(c, you) > range) continue;
        if (t.action.target === 'enemy' && c.faction === 'party') continue;
        if (t.action.target === 'ally' && c.faction !== 'party' && !(t.action.aid && c.faction === 'neutral' && c.attitude !== 'hostile')) continue;
        o.targets.add(c.id);
      }
    }
  } else if (store.controlling && view.construct) {
    for (const i of view.construct.reachable) o.reachable.add(i);
  } else if (view.combat?.yourTurn) {
    for (const i of view.combat.reachable) o.reachable.add(i);
  }
  const area = t?.action.spell?.area ?? t?.action.area;
  if (area && you && view.dungeon && (store.hover || area.shape === 'emanation')) {
    for (const i of areaPreview(view, you, area, store.hover)) o.areaTiles.add(i);
  }
  return o;
}

/** Client-side preview of an area spell (the server does the real line-of-effect checks). */
function areaPreview(view: PlayerView, you: { x: number; y: number }, area: { shape: string; radius: number }, aim: { x: number; y: number } | null): number[] {
  const d = view.dungeon!;
  const out: number[] = [];
  if (area.shape === 'line') {
    if (!aim) return out;
    const dx = aim.x - you.x, dy = aim.y - you.y;
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    for (let i = 1; steps && i <= area.radius; i++) {
      const x = Math.round(you.x + (dx * i) / steps), y = Math.round(you.y + (dy * i) / steps);
      if (x < 0 || y < 0 || x >= d.width || y >= d.height) break;
      const tile = d.tiles[y * d.width + x];
      if (tile === '#' || tile === '+') break;
      out.push(y * d.width + x);
    }
    return out;
  }
  const origin = area.shape === 'burst' && aim ? aim : you;
  for (let y = origin.y - area.radius; y <= origin.y + area.radius; y++) {
    for (let x = origin.x - area.radius; x <= origin.x + area.radius; x++) {
      if (x < 0 || y < 0 || x >= d.width || y >= d.height) continue;
      const tile = d.tiles[y * d.width + x];
      if (tile === '#' || tile === '+' || gridDistance(origin, { x, y }) > area.radius) continue;
      if (area.shape === 'cone') {
        if (!aim || (x === you.x && y === you.y)) continue;
        const ax = aim.x - you.x, ay = aim.y - you.y, bx = x - you.x, by = y - you.y;
        if ((ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by) || 1) < 0.69) continue;
      }
      out.push(y * d.width + x);
    }
  }
  return out;
}

// ------------------------------------------------------------ map input

const canvas = $<HTMLCanvasElement>('map');

/** The map tooltip's HTML as last written (the browser's own innerHTML reads back normalized). */
let tipHtml = '';

/** Shows what's on the hovered square, or hides the tooltip when there's nothing. */
function showTooltip(view: PlayerView, tile: { x: number; y: number }): void {
  const tip = $('tooltip');
  const html = view.dungeon ? tooltipFor(view, tile.x, tile.y) : '';
  if (!html) return tip.classList.add('hidden');
  if (html !== tipHtml) tip.innerHTML = tipHtml = html;
  tip.classList.remove('hidden');
}

/** Where the pointer last was over the map (client coordinates), or null once it left. */
let pointer: { x: number; y: number } | null = null;

/** The square under the pointer now: the camera may have moved under a still mouse. */
function tileUnderPointer(p: { x: number; y: number }): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  return renderer.screenToTile(p.x - r.left, p.y - r.top);
}

/** A new view while hovering: the square's creatures may have moved, or the party recalled something about them. */
function refreshTooltip(view: PlayerView): void {
  if (!pointer) return;
  store.hover = tileUnderPointer(pointer);
  showTooltip(view, store.hover);
}

canvas.addEventListener('mousemove', (e) => {
  const view = store.view;
  if (!view?.dungeon) return;
  const r = canvas.getBoundingClientRect();
  pointer = { x: e.clientX, y: e.clientY };
  const tile = tileUnderPointer(pointer);
  store.hover = tile;
  const tip = $('tooltip');
  tip.style.left = `${Math.min(r.width - 290, e.clientX - r.left + 14)}px`;
  tip.style.top = `${e.clientY - r.top + 14}px`;
  showTooltip(view, tile);
});
canvas.addEventListener('wheel', (e) => {
  e.preventDefault();
  zoom(e.deltaY < 0 ? 4 : -4);
}, { passive: false });
canvas.addEventListener('mouseleave', () => {
  store.hover = null;
  pointer = null;
  $('tooltip').classList.add('hidden');
});
// Right mouse button: a click cancels (targeting, a walk); a drag moves the view, which then stays
// put until you press Follow.
let rightDrag: { x: number; y: number; moved: boolean } | null = null;
let rightDragEnded = 0;
canvas.addEventListener('mousedown', (e) => {
  if (e.button === 2) rightDrag = { x: e.clientX, y: e.clientY, moved: false };
});
window.addEventListener('mousemove', (e) => {
  if (!rightDrag) return;
  if (!(e.buttons & 2)) {
    rightDrag = null;
    canvas.classList.remove('panning');
    return;
  }
  const dx = e.clientX - rightDrag.x, dy = e.clientY - rightDrag.y;
  if (!rightDrag.moved && Math.hypot(dx, dy) < 5) return;
  rightDrag.moved = true;
  canvas.classList.add('panning');
  renderer.pan(dx, dy);
  rightDrag.x = e.clientX;
  rightDrag.y = e.clientY;
});
window.addEventListener('mouseup', (e) => {
  if (e.button !== 2 || !rightDrag) return;
  if (rightDrag.moved) rightDragEnded = performance.now();
  rightDrag = null;
  canvas.classList.remove('panning');
});
canvas.addEventListener('contextmenu', (e) => {
  e.preventDefault();
  // The end of a drag isn't a click.
  if (rightDrag?.moved || performance.now() - rightDragEnded < 250) return;
  cancelTargeting();
  store.walkTo = null;
  store.openDoorAt = null;
});
$('recenter').addEventListener('click', () => renderer.recenter());

/** Is any leaf of the door at (x, y) next to you? (A double door opens from any of them.) */
function doorNear(d: DungeonView, x: number, y: number, you: { x: number; y: number }): boolean {
  const run = doorRun(d, x, y);
  return !!run && doorSquares(d.width, x, y, run).some((i) => Math.max(Math.abs(i % d.width - you.x), Math.abs(Math.floor(i / d.width) - you.y)) === 1);
}

canvas.addEventListener('click', (e) => {
  const view = store.view;
  const d = view?.dungeon;
  const you = me(view);
  if (!view || !d) return;
  const r = canvas.getBoundingClientRect();
  const { x, y } = renderer.screenToTile(e.clientX - r.left, e.clientY - r.top);
  if (store.targeting) {
    const tg = store.targeting;
    if (tg.choosing) {
      // The Use banner: a click on your token or on a creature you could feed picks it too.
      const clicked = d.creatures.find((c) => covers(c, x, y) && c.health !== 'dead');
      const feedTo = tg.itemUid ? view.sheet?.items.find((i) => i.uid === tg.itemUid)?.feedTo ?? tg.action.recipients : undefined;
      if (tg.action.id === 'use-item' && tg.itemUid && clicked && (clicked.isYou || feedTo?.includes(clicked.id))) {
        cancelTargeting();
        return sendUse(tg.itemUid, clicked.isYou ? undefined : clicked.id);
      }
      return toast(`Choose an option for ${tg.action.name} first (Esc to cancel)`);
    }
    const target = targetForTile(view, x, y);
    if (!target) return toast('Select a valid target');
    const { actorId, choice } = store.targeting;
    // The Strike button with no weapon picked: a point-blank shot asks which Strike first, as a click on the foe does.
    const foe = tg.action.id === 'strike' && !tg.strikeKey && !actorId && target.creatureId ? d.creatures.find((c) => c.id === target.creatureId) : undefined;
    if (foe) {
      cancelTargeting();
      return strikeAt(view, foe);
    }
    send({ type: 'action', actionId: store.targeting.action.id, target: { ...target, ...(choice !== undefined ? { choice } : {}) }, ...(actorId ? { actorId } : {}) });
    cancelTargeting();
    return;
  }
  if (!you) return;
  const adjacent = Math.max(Math.abs(x - you.x), Math.abs(y - you.y)) <= 1;
  const enabled = (id: string) => view.actions.some((a) => a.id === id && a.enabled);
  const creature = d.creatures.find((c) => covers(c, x, y) && c.health !== 'dead');
  // Alt+click: talk to that creature (any creature in earshot, friend or foe, or a bot companion); Alt+Shift+click: to its band.
  if (e.altKey && creature && (!creature.isPc || creature.bot)) {
    const band = e.shiftKey && creature.band ? d.bands?.find((b) => b.id === creature.band) : undefined;
    if (band) {
      if (!band.earshot) return toast('They are too far away to hear you');
      talkToBand(creature);
      renderTalk(view);
      return focusChat();
    }
    if (!creature.earshot) return toast(`${creature.name} is too far away to hear you`);
    talkToCreature(creature);
    renderTalk(view);
    return focusChat();
  }
  const feature = d.features.find((f) => f.x === x && f.y === y);
  // Items come before the structure they lie on (a campfire, the stairs), so they can always be picked up.
  const items = d.items.some((i) => i.x === x && i.y === y);
  const tile = d.tiles[y * d.width + x];
  store.pickupAt = null;
  store.openDoorAt = null;

  if (view.combat) {
    if (!view.combat.yourTurn) return toast("It isn't your turn");
    // Clicking your construct takes control of it; clicking yourself takes control back.
    if (view.construct && creature?.id === view.construct.id && !store.controlling) return setControl(view, true);
    if (store.controlling && creature?.isYou) return setControl(view, false);
    if (store.controlling && view.construct) {
      const k = view.construct;
      const minion = actorOf(view);
      const can = (id: string) => k.actions.some((a) => a.id === id && a.enabled);
      const actorId = k.id;
      if (creature?.faction === 'hostile') {
        if (can('strike')) return send({ type: 'action', actionId: 'strike', target: { creatureId: creature.id }, actorId });
        return toast(k.actions.find((a) => a.id === 'strike')?.reason ?? `${k.name} can't attack right now`);
      }
      if (minion && k.reachable.includes(y * d.width + x) && !(x === minion.x && y === minion.y)) return send({ type: 'action', actionId: 'stride', target: { x, y }, actorId });
      return;
    }
    if (creature?.faction === 'hostile' && enabled('strike')) return strikeAt(view, creature);
    // A square you could Stride to is still a Stride; take its items from the loot panel (G takes a lone item).
    const strideable = view.combat.reachable.includes(y * d.width + x) && !(x === you.x && y === you.y);
    if (items && adjacent && !strideable) return pickUp(view, { x, y });
    if (feature?.interactive && adjacent && enabled('interact')) return send({ type: 'action', actionId: 'interact', target: { x, y } });
    if (tile === '+' && doorNear(d, x, y, you) && enabled('open-door')) return send({ type: 'action', actionId: 'open-door', target: { x, y } });
    if (strideable) return send({ type: 'action', actionId: 'stride', target: { x, y } });
    return;
  }
  if (creature?.faction === 'neutral') {
    if (creature.earshot) {
      talkToCreature(creature);
      renderTalk(view);
    }
    return focusChat();
  }
  if (items) {
    if (adjacent) return pickUp(view, { x, y });
    // Walk over, then pick up.
    store.pickupAt = { x, y };
    store.walkTo = standNear(d, { x, y }, you);
    return;
  }
  if (feature?.kind === 'sealed-door') {
    if (adjacent) send({ type: 'action', actionId: 'interact', target: { x, y } });
    return focusChat();
  }
  if (feature?.interactive && adjacent) return send({ type: 'action', actionId: 'interact', target: { x, y } });
  // A door you know is trapped won't open by walking into it: a click on it opens it on purpose (Open Door, which
  // sets the trap off), from beside it or once you've walked there.
  if (trappedDoor(d, x, y)) {
    if (!doorNear(d, x, y, you)) {
      store.openDoorAt = { x, y };
      store.walkTo = standNear(d, { x, y }, you);
      return;
    }
    if (enabled('open-door')) return send({ type: 'action', actionId: 'open-door', target: { x, y } });
    return toast(view.actions.find((a) => a.id === 'open-door')?.reason ?? "You can't open that door right now");
  }
  if (tile === ' ' || tile === '#') return;
  store.walkTo = { x, y };
});

/**
 * Click-to-walk: the route is planned once per click (see pathing.ts) and followed a step at a
 * time. The next step goes out only once the last one has shown up, so moves never stack up; the
 * route is planned again if you're knocked off it or something steps into the way, and round a square
 * that refused a step (`walkStep`).
 */
let walk: Walk | null = null;

function stopWalking(): void {
  store.walkTo = null;
  walk = null;
}

setInterval(() => {
  const view = store.view;
  const goal = store.walkTo;
  const you = me(view);
  if (view?.dungeon && you && !view.combat && store.pickupAt && chebyshev(you, store.pickupAt) <= 1) {
    const at = store.pickupAt;
    store.pickupAt = null;
    stopWalking();
    return pickUp(view, at);
  }
  // Beside the trapped door you clicked: open it on purpose.
  if (view?.dungeon && you && !view.combat && store.openDoorAt && doorNear(view.dungeon, store.openDoorAt.x, store.openDoorAt.y, you)) {
    const at = store.openDoorAt;
    store.openDoorAt = null;
    stopWalking();
    if (view.dungeon.tiles[at.y * view.dungeon.width + at.x] === '+') send({ type: 'action', actionId: 'open-door', target: at });
    return;
  }
  if (!goal) {
    store.pickupAt = null;
    store.openDoorAt = null;
    walk = null;
  }
  if (!view?.dungeon || !goal || !you || view.combat) return;
  // Outside encounters walking is free: a walk goes on through the rounds of dungeon time it passes.
  if (you.x === goal.x && you.y === goal.y) return stopWalking();
  const d = view.dungeon;
  if (!walk || walk.goal.x !== goal.x || walk.goal.y !== goal.y) walk = newWalk(d, you, goal);
  const next = walkStep(d, you, walk, performance.now());
  if (next === 'wait') return;
  if (!next) return stopWalking();
  send({ type: 'move', dx: next.x - you.x, dy: next.y - you.y });
}, 60);

// ------------------------------------------------------------ keyboard

/** Taps are queued (a few deep) and sent at walking pace; held keys don't pile up. */
const moveQueue: [number, number][] = [];
function queueMove(dx: number, dy: number, repeat: boolean): void {
  if (repeat && moveQueue.length) return;
  if (moveQueue.length < 4) moveQueue.push([dx, dy]);
}
setInterval(() => {
  const step = moveQueue.shift();
  if (store.view?.combat) moveQueue.length = 0;
  else if (step) send({ type: 'move', dx: step[0], dy: step[1] });
}, 150);

const MOVE_KEYS: Record<string, [number, number]> = {
  ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
  w: [0, -1], s: [0, 1], a: [-1, 0], d: [1, 0], q: [-1, -1], e: [1, -1], z: [-1, 1], c: [1, 1],
  Numpad8: [0, -1], Numpad2: [0, 1], Numpad4: [-1, 0], Numpad6: [1, 0],
  Numpad7: [-1, -1], Numpad9: [1, -1], Numpad1: [-1, 1], Numpad3: [1, 1],
};

window.addEventListener('keydown', (e) => {
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || builderOpen()) return;
  const view = store.view;
  if (!view || store.showLogin || !store.joined) return;
  if (e.key === 'Escape') {
    // One thing at a time, the dialogs first. The small things go before the panel they sit in: a Sell waiting for a
    // yes, the Drop chooser, an aimed action, and only then the Inventory or Consumables panel (it can stay open
    // while you play). The loot panel collapses last, when nothing else wants Esc.
    if (store.transfer) escapeTransfer(view);
    else if (store.enchant?.swap !== undefined) {
      // A bridging rank's swap list closes before its dialog (or the enchanting helper's pick).
      store.enchant.swap = undefined;
      renderEnchanting(view);
    } else if (store.enchant) {
      store.enchant = null;
      renderEnchanting(view);
    } else if (store.enchantHelper) {
      helperBack();
      renderEnchanting(view);
    } else if (store.campOpen) {
      store.campOpen = false;
      renderCamp(view);
    } else if (store.journalOpen) {
      store.journalOpen = false;
      renderJournal(view);
    } else if (store.sellConfirm && document.querySelector('[data-sell-cancel]')) askSell(view, null);
    else if (store.dropOpen) openDrop(view, null);
    else if (store.targeting) cancelTargeting();
    else if (store.bag) {
      store.bag = null;
      renderBag(view);
    } else if (view.pending?.hands[0] && store.modalDismissedFor !== view.pending.hands[0].id) {
      store.modalDismissedFor = view.pending.hands[0].id;
      renderModal(view);
    } else if (view.pending?.boostsLeft && !store.boost.later) {
      store.boost = { open: false, chosen: [], later: true };
      renderModal(view);
    } else if (lootState(view) === 'open') collapseLoot(view);
    store.walkTo = null;
    store.openDoorAt = null;
    return;
  }
  const key = e.code.startsWith('Numpad') ? e.code : e.key.length === 1 ? e.key.toLowerCase() : e.key;
  if (key === 'j' && view.journal && $('modal').classList.contains('hidden') && !store.campOpen && !store.transfer && !store.enchant && !store.enchantHelper) return toggleJournal();
  if (!$('modal').classList.contains('hidden') || store.campOpen || store.transfer || store.enchant || store.enchantHelper || store.journalOpen) return;
  const dir = MOVE_KEYS[key];
  if (dir && view.dungeon) {
    e.preventDefault();
    store.walkTo = null;
    store.pickupAt = null;
    store.openDoorAt = null;
    const you = actorOf(view);
    const actorId = controlledId(view);
    if (!view.combat) queueMove(dir[0], dir[1], e.repeat);
    else if (e.shiftKey && you) send({ type: 'action', actionId: 'step', target: { x: you.x + dir[0], y: you.y + dir[1] }, ...(actorId ? { actorId } : {}) });
    else toast('During an encounter, click a square to Stride, or hold Shift and press a direction to Step.');
    return;
  }
  if (key === 't' || e.key === '/') {
    e.preventDefault();
    return focusChat();
  }
  if (e.key === 'Tab' && view.combat?.yourTurn && view.construct) {
    e.preventDefault();
    return setControl(view, !store.controlling);
  }
  if (e.key === '=' || e.key === '+') return zoom(4);
  if (e.key === '-' || e.key === '_') return zoom(-4);
  if ((e.key === ' ' || e.key === 'Enter') && view.combat?.yourTurn) {
    e.preventDefault();
    send({ type: 'endTurn' });
    return;
  }
  // P switches your autopilot (the computer plays your character) on or off.
  if (key === 'p' && view.party.some((p) => p.isYou && p.characterName)) return send({ type: 'autopilot', on: !yourAutopilot(view) });
  const action = (id: string) => activeActions(view).find((a) => a.id === id && a.enabled);
  if (key === 'f') {
    // F toggles the light in your light bar: Snuff it while it burns, otherwise Light one (the gear rows reach the others).
    const a = view.sheet?.light?.lit ? action('extinguish') : action('light') ?? action('extinguish');
    if (a) startAction(a);
    return;
  }
  if (key === 'g') {
    // G opens the loot panel when it's collapsed; open, it takes everything (in an encounter, a lone item, as much of
    // it as fits: an Interact).
    if (!view.pickup?.squares.length) return toast('There is nothing within reach to pick up');
    const state = lootState(view);
    if (!state) return toast("You can't pick things up right now");
    if (state === 'collapsed') return expandLoot(view, null);
    if (!view.combat) return send({ type: 'pickUpAll' });
    const items = view.pickup.squares.flatMap((s) => s.items);
    if (items.length > 1) return toast('In an encounter, each item takes an Interact action: click Take on the one you want');
    if (items[0].fits === 0) return toast('Not enough free gear slots');
    return takeItem(items[0]);
  }
  if (key === 'x') {
    const a = action('interact');
    if (a) startAction(a);
    return;
  }
  // R (or L) reloads a firearm or crossbow; a greyed Reload says why.
  if (key === 'r' || key === 'l') return reloadKey(view);
  // V switches a combination weapon's usage (Switch Mode); with one in each hand the banner asks which.
  if (key === 'v') {
    const a = activeActions(view).find((x) => x.id === 'switch-mode');
    if (!a) return toast(store.controlling ? "Your construct can't switch weapons" : "You aren't wielding a combination weapon");
    return startAction(a);
  }
  if (key === 'b') return toggleBag(view, e.shiftKey ? 'consumables' : 'all');
  if (/^[1-9]$/.test(key)) {
    // The banner's options first (an action's choices, Reload's hands), then the action bar.
    if (store.targeting?.choosing) return pickChoice(Number(key));
    const a = actionBarList(view)[Number(key) - 1];
    if (a) startAction(a);
  }
});

// ------------------------------------------------------------ pick list filters (the level-up modal and the retrain panel)

const onListControl = (e: Event): void => {
  const t = e.target as HTMLElement;
  const key = t.dataset?.pl;
  if (!key || t.closest('#builder') || !store.view) return;
  const state = key === 'pick' ? store.pickUi.list : key === 'retrain' ? store.retrainList : null;
  if (!state || !applyControl(state, t)) return;
  if (key === 'pick') renderModal(store.view);
  else renderHub(store.view);
};
document.addEventListener('input', onListControl);
document.addEventListener('change', onListControl);

// ------------------------------------------------------------ delegated clicks

/** When the bridging assistant last sent a step (performance.now()). */
let lastBridgeStep = 0;

/** The Enchant dialog and the town's enchanting helper, which share the enchanting pick (`store.enchant`). */
function renderEnchanting(view: PlayerView): void {
  renderEnchant(view);
  renderEnchantHelper(view);
}

document.addEventListener('click', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-token-edit],[data-construct-name],[data-reset-order],[data-act],[data-endturn],[data-item-act],[data-feature],[data-strike],[data-card],[data-reroll],[data-later],[data-open-modal],[data-open-boosts],[data-open-picks],[data-boost-pick],[data-boost-apply],[data-boost-later],[data-pl-focus],[data-retrain-target],[data-retrain-take],[data-retrain-cancel],[data-buy],[data-shop-tab],[data-emporium],[data-sell],[data-sell-cancel],[data-innovate],[data-equip],[data-swap-hands],[data-ready],[data-reaction],[data-debilitation],[data-master-strike],[data-select],[data-open-builder],[data-rebuild],[data-addbot],[data-removebots],[data-autopilot],[data-trade-buy],[data-trade-sell],[data-bank-deposit],[data-bank-withdraw],[data-camp],[data-camp-close],[data-journal-close],[data-scavenge],[data-pickup],[data-pickup-all],[data-loot-toggle],[data-drop],[data-drop-open],[data-drop-close],[data-throw],[data-remove-player],[data-kickvote],[data-descent],[data-world-vote],[data-new-world],[data-control],[data-enchant],[data-anoint],[data-repair],[data-ench-target],[data-ench-powder],[data-ench-by],[data-ench-water],[data-ench-scroll],[data-ench-req],[data-ench-go],[data-ench-close],[data-ench-helper],[data-ench-gear],[data-ench-slot],[data-ench-pick],[data-ench-swap],[data-dismiss-companion],[data-recall-topic],[data-bag]');
  if (!el) return;
  const ds = el.dataset;
  const view = store.view;
  if (ds.tokenEdit) return ds.tokenConstruct !== undefined ? editConstructToken() : editToken(ds.tokenEdit);
  if (ds.constructName !== undefined) return nameConstruct();
  if (ds.bag) {
    store.bag = ds.bag === 'close' ? null : ds.bag as 'all' | 'consumables';
    if (view) renderBag(view);
    return;
  }
  if (ds.select) return conn.send({ t: 'selectCharacter', characterId: ds.select });
  if (ds.openBuilder !== undefined) return openBuilder('create');
  if (ds.rebuild !== undefined) {
    const from = view?.hub?.rebuild;
    return from ? openBuilder('rebuild', from) : undefined;
  }
  if (!view) return;
  if (ds.resetOrder !== undefined) {
    resetActionOrder(view);
    return renderActionbar(view);
  }
  if (ds.addbot !== undefined) return conn.send({ t: 'addBot' });
  if (ds.removebots !== undefined) return conn.send({ t: 'removeBots' });
  if (ds.autopilot) return send({ type: 'autopilot', on: ds.autopilot === 'on' });
  if (ds.control) return setControl(view, ds.control === 'on');
  if (ds.act) {
    const a = activeActions(view).find((x) => x.id === ds.act);
    if (!a) return;
    if (store.targeting?.action.id === a.id) return cancelTargeting();
    return startAction(a);
  }
  if (ds.endturn !== undefined) return send({ type: 'endTurn' });
  if (ds.itemAct) {
    if (ds.itemAct === 'pick-up') return send({ type: 'action', actionId: 'pick-up', target: { itemUid: ds.uid } });
    // Use asks whom it's for when someone next to you could take it (feeding a potion).
    if (ds.itemAct === 'use-item' && ds.uid) return useItemAt(view, ds.uid);
    // A row's button can name the choice too (a gun's Unload: a standard round in place of its specialty one).
    return send({ type: 'action', actionId: ds.itemAct, target: { itemUid: ds.uid, ...(ds.choice ? { choice: ds.choice } : {}) } });
  }
  if (ds.feature) {
    const [x, y] = ds.feature.split(',').map(Number);
    return send({ type: 'action', actionId: 'interact', target: { x, y } });
  }
  if (ds.strike) {
    const a = view.actions.find((x) => x.id === 'strike');
    if (store.controlling) setControl(view, false);
    if (a?.enabled) startAction(a, ds.strike);
    return;
  }
  if (ds.card) {
    const [handId, featId] = ds.card.split(':');
    const hand = view.pending?.hands.find((h) => h.id === handId);
    return send(hand?.pick ? { type: 'choosePick', pickId: handId, optionId: featId } : { type: 'chooseCard', handId, featId });
  }
  if (ds.later) {
    store.modalDismissedFor = ds.later;
    return renderModal(view);
  }
  if (ds.openModal !== undefined) {
    store.modalDismissedFor = null;
    return renderModal(view);
  }
  if (ds.openBoosts !== undefined) {
    store.boost.open = true;
    store.boost.later = false;
    return renderModal(view);
  }
  if (ds.openPicks !== undefined) {
    store.modalDismissedFor = null;
    store.boost.later = false;
    return renderModal(view);
  }
  if (ds.boostPick) {
    const need = boostsNeeded(view);
    const a = ds.boostPick as Attribute;
    const chosen = store.boost.chosen.includes(a) ? store.boost.chosen.filter((x) => x !== a) : [...store.boost.chosen, a].slice(-need);
    store.boost.chosen = chosen;
    return renderModal(view);
  }
  if (ds.boostLater !== undefined) {
    store.boost = { open: false, chosen: [], later: true };
    return renderModal(view);
  }
  if (ds.boostApply !== undefined) {
    const chosen = store.boost.chosen;
    store.boost.chosen = [];
    for (const attribute of chosen) send({ type: 'boost', attribute });
    return;
  }
  if (ds.plFocus !== undefined) {
    const inModal = !!el.closest('#modal');
    const list = inModal ? store.pickUi.list : store.retrainList;
    list.focus = list.focus === ds.plFocus ? undefined : ds.plFocus;
    return inModal ? renderModal(view) : renderHub(view);
  }
  if (ds.retrainTarget) {
    const [kind, remove] = ds.retrainTarget.split('|');
    store.retrain = { kind: kind as 'feat', remove };
    store.retrainOptions = null;
    store.retrainList = newListState();
    conn.send({ t: 'retrainOptions', kind: kind as 'feat', remove });
    return renderHub(view);
  }
  if (ds.retrainTake && store.retrain) {
    const { kind, remove } = store.retrain;
    store.retrain = null;
    store.retrainOptions = null;
    send({ type: 'retrain', kind, remove, add: ds.retrainTake });
    return renderHub(view);
  }
  if (ds.retrainCancel !== undefined) {
    store.retrain = null;
    return renderHub(view);
  }
  if (ds.buy) return send({ type: 'buy', itemId: ds.buy, ...(ds.ware !== undefined ? { ware: Number(ds.ware) } : {}), ...(ds.buyEmporium ? { emporium: true } : {}) });
  if (ds.shopTab) {
    store.shopTab = ds.shopTab as typeof store.shopTab;
    return renderHub(view);
  }
  if (ds.emporium) {
    const action = ds.emporium as 'increment' | 'extract';
    const o = view?.hub?.emporium.find((x) => x.itemUid === ds.emporiumItem && x.slot === ds.emporiumSlot);
    if (!o) return;
    const ask = `Pull the ${o.name} ${o.slot} (rank ${o.rank}) out of the ${o.itemName} for ${o.extract.fee} gp? The slot is left empty, and you get a rank ${o.rank} ${o.name} enchant scroll.`;
    if (action === 'extract' && !window.confirm(ask)) return;
    return send({ type: 'emporium', action, itemUid: o.itemUid, slot: o.slot });
  }
  if (ds.sell || ds.tradeSell) return sellClick(view, ds, e.shiftKey, e.detail);
  if (ds.sellCancel !== undefined) return askSell(view, null);
  if (ds.innovate) return send({ type: 'setInnovation', itemUid: ds.innovate });
  if (ds.camp) {
    store.campOpen = false;
    renderCamp(view);
    return send({ type: 'camp', method: ds.camp as 'rest' });
  }
  if (ds.campClose !== undefined) {
    // Clicks inside the dialog box don't close it; the backdrop and Cancel do.
    if (ds.campClose === 'backdrop' && e.target !== el) return;
    store.campOpen = false;
    return renderCamp(view);
  }
  if (ds.journalClose !== undefined) {
    // Clicks inside the journal don't close it; the backdrop and Close do.
    if (ds.journalClose === 'backdrop' && e.target !== el) return;
    store.journalOpen = false;
    return renderJournal(view);
  }
  if (ds.scavenge) return send({ type: 'scavenge', action: ds.scavenge as 'join' });
  // The town's enchanting helper (client/enchant-helper.ts): open, close, back, then gear, slot and scroll.
  if (ds.enchHelper !== undefined) {
    if (ds.enchHelper === 'backdrop' && e.target !== el) return;
    if (ds.enchHelper === 'open') store.enchantHelper = {};
    else if (ds.enchHelper === 'back') helperBack();
    else {
      store.enchantHelper = null;
      if (store.enchant?.helper) store.enchant = null;
    }
    return renderEnchanting(view);
  }
  if (ds.enchGear && store.enchantHelper) {
    if (store.enchantHelper.gearUid !== ds.enchGear) {
      store.enchantHelper = { gearUid: ds.enchGear };
      if (store.enchant?.helper) store.enchant = null;
    }
    return renderEnchanting(view);
  }
  if (ds.enchSlot && store.enchantHelper?.gearUid) {
    if (store.enchantHelper.slot !== ds.enchSlot) {
      store.enchantHelper = { gearUid: store.enchantHelper.gearUid, slot: ds.enchSlot as 'prefix' | 'suffix' };
      if (store.enchant?.helper) store.enchant = null;
    }
    return renderEnchanting(view);
  }
  if (ds.enchPick && store.enchantHelper?.gearUid && store.enchantHelper.slot) {
    const { gearUid, slot } = store.enchantHelper;
    // Picking the scroll again keeps its choices (a bridging rank's swaps among them).
    if (store.enchant?.helper && store.enchant.itemUid === ds.enchPick) return;
    store.enchant = { mode: 'enchant', itemUid: ds.enchPick, targetUid: gearUid, helper: { gearUid, slot } };
    return renderEnchanting(view);
  }
  if (ds.enchSwap !== undefined && store.enchant) {
    // A bridging rank's slot: open (or close) the list of scrolls to read for it instead.
    const rank = Number(ds.enchSwap);
    store.enchant.swap = store.enchant.swap === rank ? undefined : rank;
    return renderEnchanting(view);
  }
  if (ds.enchant || ds.anoint) {
    const uid = ds.enchant ?? ds.anoint!;
    const item = view.sheet?.items.find((i) => i.uid === uid);
    const targets = (ds.enchant ? item?.enchant?.targets : item?.anoint?.targets) ?? [];
    // Gear it can go on now, else gear the bridging assistant leads up to.
    const target = targets.find((t) => !t.error) ?? (ds.enchant ? targets.find(enchantable) : undefined);
    store.enchant = { mode: ds.enchant ? 'enchant' : 'anoint', itemUid: uid, targetUid: target?.uid };
    return renderEnchanting(view);
  }
  if (ds.enchTarget && store.enchant) {
    store.enchant.targetUid = ds.enchTarget;
    store.enchant.swap = undefined;
    return renderEnchanting(view);
  }
  // The Enchant dialog's choices: for its own check, or for a bridging step (data-ench-step, the step's rank index).
  const enchChoice = (st: NonNullable<typeof store.enchant>) => (ds.enchStep === undefined ? st : ((st.bridge ??= {})[Number(ds.enchStep)] ??= {}));
  if (ds.enchBy !== undefined && store.enchant) {
    const c = enchChoice(store.enchant);
    c.enchanterId = ds.enchBy || undefined;
    if (!c.enchanterId && c.powderFrom === 'enchanter') c.powderFrom = undefined;
    return renderEnchanting(view);
  }
  if (ds.enchPowder !== undefined && store.enchant) {
    const c = enchChoice(store.enchant);
    c.powderFrom = ds.theirs !== undefined ? 'enchanter' : 'requester';
    c.powderUid = ds.theirs !== undefined ? undefined : ds.enchPowder || undefined;
    return renderEnchanting(view);
  }
  if (ds.enchWater !== undefined && store.enchant) {
    enchChoice(store.enchant).anoint = (el as HTMLInputElement).checked;
    return renderEnchanting(view);
  }
  if (ds.enchScroll && store.enchant) {
    // A scroll swapped into a bridging rank: its swap list closes.
    enchChoice(store.enchant).scrollUid = ds.enchScroll;
    store.enchant.swap = undefined;
    return renderEnchanting(view);
  }
  if (ds.enchGo !== undefined && store.enchant?.targetUid) {
    const st = store.enchant;
    if (st.mode === 'anoint') {
      store.enchant = null;
      renderEnchanting(view);
      return send({ type: 'anoint', waterUid: st.itemUid, targetUid: st.targetUid! });
    }
    const go = enchantCommand(view);
    if (!go) return;
    // A bridging step keeps the dialog open: the next view brings the plan's next rank (or the scroll itself).
    // A quick second click would read that next rank unasked, so a step waits a moment after the last one.
    if (go.bridge) {
      if (performance.now() - lastBridgeStep < 800) return;
      lastBridgeStep = performance.now();
      (el as HTMLButtonElement).disabled = true;
    } else {
      // The scroll itself: the dialog closes (the enchanting helper stays open, back on the slot's scrolls).
      store.enchant = null;
      renderEnchanting(view);
    }
    return send(go.command);
  }
  if (ds.enchReq) {
    const r = view.enchantRequests.find((x) => x.id === ds.enchReq);
    if (!r) return;
    if (ds.answer === 'cancel') return send({ type: 'cancelEnchantRequest', id: r.id });
    if (ds.answer === 'powder') {
      store.enchantAnswer[r.id] = ds.uid ?? '';
      return renderEnchantRequests(view);
    }
    const powderUid = ds.answer === 'accept' ? r.yourPowders?.find((p) => p.uid === store.enchantAnswer[r.id])?.uid : undefined;
    return send({ type: 'answerEnchantRequest', id: r.id, accept: ds.answer === 'accept', ...(powderUid ? { powderUid } : {}) });
  }
  if (ds.enchClose !== undefined) {
    if (ds.enchClose === 'backdrop' && e.target !== el) return;
    store.enchant = null;
    return renderEnchanting(view);
  }
  if (ds.repair) return send({ type: 'repairGear', itemUid: ds.repair });
  if (ds.recallTopic && view.recall) return send({ type: 'askRecall', id: view.recall.id, topic: ds.recallTopic });
  if (ds.dismissCompanion) {
    // Two clicks: the first arms the button, the second parts ways for good.
    if (!el.classList.contains('armed')) {
      el.classList.add('armed');
      el.textContent = 'Part ways?';
      window.setTimeout(() => {
        if (!el.isConnected) return;
        el.classList.remove('armed');
        el.textContent = '✕';
      }, 3000);
      return;
    }
    return send({ type: 'dismissCompanion', companionId: ds.dismissCompanion });
  }
  if (ds.removePlayer) {
    // Two clicks: the first arms the button, the second calls the vote.
    if (!el.classList.contains('armed')) {
      el.classList.add('armed');
      el.textContent = 'Remove?';
      window.setTimeout(() => {
        if (!el.isConnected) return;
        el.classList.remove('armed');
        el.textContent = '✕';
      }, 3000);
      return;
    }
    return send({ type: 'voteRemove', playerId: ds.removePlayer, agree: true });
  }
  if (ds.kickvote && view.kickVote) return send({ type: 'voteRemove', playerId: view.kickVote.targetId, agree: ds.kickvote === 'yes' });
  if (ds.worldVote) return send({ type: 'voteNewWorld', agree: ds.worldVote === 'yes' });
  if (ds.descent === 'wait' || ds.descent === 'go') return send({ type: 'descentChoice', choice: ds.descent });
  if (ds.newWorld !== undefined) {
    // Two clicks: the first arms the button, the second calls the vote.
    if (!el.classList.contains('armed')) {
      el.classList.add('armed');
      el.textContent = 'Call the vote?';
      window.setTimeout(() => {
        if (!el.isConnected) return;
        el.classList.remove('armed');
        el.textContent = 'Start a New World';
      }, 3000);
      return;
    }
    return send({ type: 'voteNewWorld', agree: true });
  }
  if (ds.pickup) {
    const [itemUid, n] = ds.pickup.split(':');
    const item = view.pickup?.squares.flatMap((s) => s.items).find((i) => i.uid === itemUid);
    if (item) takeItem(item, Number(n) || item.fits);
    return;
  }
  if (ds.pickupAll !== undefined) return send({ type: 'pickUpAll' });
  if (ds.dropOpen) return openDrop(view, ds.dropOpen);
  if (ds.dropClose !== undefined) return openDrop(view, null);
  if (ds.drop) {
    // "uid:how": the uid is everything before the last colon.
    const cut = ds.drop.lastIndexOf(':');
    return dropSome(view, ds.drop.slice(0, cut), ds.drop.slice(cut + 1));
  }
  if (ds.throw) {
    const a = view.actions.find((x) => x.id === 'throw-bomb');
    if (store.controlling) setControl(view, false);
    if (a) startAction(a, undefined, ds.throw);
    return;
  }
  if (ds.lootToggle !== undefined) return lootState(view) === 'open' ? collapseLoot(view) : expandLoot(view, null);
  if (ds.tradeBuy) return send({ type: 'trade', action: 'buy', item: ds.tradeBuy });
  if (ds.bankDeposit) return send({ type: 'bank', action: 'deposit', itemUid: ds.bankDeposit, ...(ds.bankQty ? { qty: Number(ds.bankQty) } : {}) });
  if (ds.bankWithdraw) {
    // "uid:qty": the uid is everything before the last colon.
    const cut = ds.bankWithdraw.lastIndexOf(':');
    const qty = Number(ds.bankWithdraw.slice(cut + 1));
    return send({ type: 'bank', action: 'withdraw', itemUid: ds.bankWithdraw.slice(0, cut), ...(qty > 0 ? { qty } : {}) });
  }
  if (ds.equip) {
    const [itemUid, slot] = ds.equip.split(':');
    return send({ type: 'equip', itemUid, slot: slot as 'hand0' });
  }
  if (ds.swapHands !== undefined) return send({ type: 'swapHands' });
  if (ds.ready) return send({ type: 'ready', ready: ds.ready === 'true' });
  if (ds.reaction) {
    const [reactionId, enabled] = ds.reaction.split(':');
    return send({ type: 'setReaction', reactionId, enabled: enabled === 'true' });
  }
  // Debilitating Strike: Auto clears your order; a debilitation joins the end of it, or leaves it if it's there.
  if (ds.debilitation) {
    const d = view.sheet?.debilitations;
    if (!d) return;
    return send({ type: 'setDebilitations', order: nextDebilitationOrder(d.order, ds.debilitation) });
  }
  if (ds.masterStrike) return send({ type: 'setDebilitations', masterStrike: ds.masterStrike === 'true' });
});

// ------------------------------------------------------------ chat

document.addEventListener('change', (e) => {
  const el = e.target as HTMLElement;
  if (el instanceof HTMLSelectElement && el.dataset.delve !== undefined) send({ type: 'setDelveDepth', depth: Number(el.value) });
});

// The town's theme box: what is typed survives re-renders until it is sent.
document.addEventListener('input', (e) => {
  const el = e.target as HTMLElement;
  if (el instanceof HTMLInputElement && el.dataset.themeInput !== undefined) store.themeDraft = el.value;
});
document.addEventListener('submit', (e) => {
  const form = e.target as HTMLElement;
  if (form.dataset?.themeForm === undefined) return;
  e.preventDefault();
  const input = form.querySelector<HTMLInputElement>('[data-theme-input]');
  send({ type: 'setWorldTheme', theme: input?.value ?? '' });
  store.themeDraft = null;
  input?.blur();
});

const chatInput = $<HTMLInputElement>('chat-input');

function focusChat(): void {
  chatInput.focus();
}

$('chat-form').addEventListener('submit', (e) => {
  e.preventDefault();
  const text = chatInput.value.trim();
  if (!text) return;
  const view = store.view;
  // In the dungeon, words go to whoever you picked (or ask your Recall Knowledge question); in town, to the other players.
  const to = view?.dungeon ? currentTalk(view).target : undefined;
  send(recallQuestion(view, text) ?? { type: 'say', text, ...(to ? { to } : {}) });
  chatInput.value = '';
});
$('talk-to').addEventListener('change', (e) => {
  store.talkTo = (e.target as HTMLSelectElement).value;
  if (store.view) renderTalk(store.view);
  chatInput.focus();
});
// A check a creature asked for: roll it, or decline.
document.addEventListener('click', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-check-roll], [data-check-decline]');
  if (!el) return;
  const roll = el.dataset.checkRoll;
  send({ type: 'answerCheck', id: roll ?? el.dataset.checkDecline!, accept: !!roll });
});
// A faction quest's card: take it, turn it down, give it up, hand the keepsake over, buy it, or go at its holder (Steal, Request, Coerce).
document.addEventListener('click', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-quest], [data-quest-act]');
  if (!el || (el as HTMLButtonElement).disabled) return;
  if (el.dataset.quest) {
    const [action, questId] = el.dataset.quest.split(':') as [Extract<Command, { type: 'quest' }>['action'], string];
    return send({ type: 'quest', action, questId });
  }
  const [actionId, creatureId] = el.dataset.questAct!.split(':');
  send({ type: 'action', actionId, target: { creatureId } });
});
chatInput.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') chatInput.blur();
});

// ------------------------------------------------------------ tokens

function editToken(characterId: string): void {
  const summary = store.welcome?.characters.find((c) => c.id === characterId);
  const sheet = store.view?.sheet?.id === characterId ? store.view.sheet : null;
  const name = sheet?.name ?? summary?.name ?? 'Character';
  openTokenizer({
    name,
    hasToken: !!(sheet?.token ?? summary?.token),
    onSave: (image) => conn.send({ t: 'setToken', characterId, image }),
  });
}

/** Your construct companion's own token (an inventor's sheet). */
function editConstructToken(): void {
  const sheet = store.view?.sheet;
  if (!sheet?.construct) return;
  openTokenizer({
    name: sheet.construct.name,
    hasToken: !!sheet.construct.token,
    onSave: (image) => conn.send({ t: 'setToken', characterId: sheet.id, image, construct: true }),
  });
}

/** Names your construct companion (its token on the map and its lines in the log follow). */
function nameConstruct(): void {
  const k = store.view?.sheet?.construct;
  if (!k) return;
  openNameDialog({
    title: 'Name your construct',
    hint: 'Up to 24 letters and numbers, with spaces, apostrophes, hyphens or periods.',
    value: k.named ? k.name : '',
    max: 24,
    onSave: (name) => send({ type: 'nameConstruct', name }),
  });
}

// ------------------------------------------------------------ hot bar drag and drop

const actionbar = $('actionbar');
let dragging: string | null = null;
actionbar.addEventListener('dragstart', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
  if (!el) return;
  dragging = el.dataset.act!;
  e.dataTransfer?.setData('text/plain', dragging);
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  el.classList.add('dragging');
});
actionbar.addEventListener('dragover', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
  if (!dragging || !el) return;
  e.preventDefault();
  actionbar.querySelectorAll('.drop-target').forEach((x) => x !== el && x.classList.remove('drop-target'));
  el.classList.add('drop-target');
});
actionbar.addEventListener('drop', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
  if (!dragging || !el || !store.view) return;
  e.preventDefault();
  moveAction(store.view, dragging, el.dataset.act!);
  dragging = null;
  renderActionbar(store.view);
});
actionbar.addEventListener('dragend', () => {
  dragging = null;
  actionbar.querySelectorAll('.dragging, .drop-target').forEach((x) => x.classList.remove('dragging', 'drop-target'));
});

// ------------------------------------------------------------ chat log resizing

const logEl = $('log');
const savedLogHeight = Number(localStorage.getItem('crawler.logHeight'));
if (savedLogHeight) logEl.style.height = `${savedLogHeight}px`;
$('log-resize').addEventListener('pointerdown', (e) => {
  e.preventDefault();
  const startY = e.clientY, startH = logEl.offsetHeight;
  const move = (ev: PointerEvent) => {
    logEl.style.height = `${Math.max(90, Math.min(window.innerHeight * 0.7, startH + startY - ev.clientY))}px`;
  };
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    localStorage.setItem('crawler.logHeight', String(logEl.offsetHeight));
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
});

// ------------------------------------------------------------ render loop

function frame(time: number): void {
  const view = store.view;
  if (view?.dungeon && !$('map-wrap').classList.contains('hidden')) {
    // The loot panel moves the camera over only when it would cover your token (a narrow map).
    const loot = $('pickup');
    const covers = lootState(view) === 'open' && loot.offsetLeft + loot.offsetWidth + renderer.tile > $('map-wrap').clientWidth / 2;
    renderer.insetLeft = covers ? loot.offsetWidth + 20 : 0;
    renderer.draw(view, overlay(view), time, fx);
    $('recenter').classList.toggle('hidden', renderer.following);
    diceTray.frame(time);
  }
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

conn.connect();
